import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import TButton from "@/components/TButton";
import {
  Bell, Save, Send, Loader2, CheckCircle2, XCircle, Shield, AlertTriangle,
  Building2, RotateCcw, BellOff,
} from "lucide-react";
import { toast } from "sonner";

const EVENT_TYPES = [
  { value: "authorization.issued",  label: "Authorization Issued",  desc: "When an analyst issues a new scan authorization" },
  { value: "authorization.revoked", label: "Authorization Revoked", desc: "When an authorization is killed before expiry" },
  { value: "scan.launched",         label: "Scan Launched",         desc: "Every ad-hoc or scheduled scan dispatch" },
];

const Configuration = () => {
  const [settings, setSettings] = useState(null);
  const [slack, setSlack] = useState("");
  const [discord, setDiscord] = useState("");
  const [enabledEvents, setEnabledEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/system/notifications");
      setSettings(data);
      setEnabledEvents(data.enabled_events || []);
      // Don't populate masked URLs — operator must paste a new URL to change one
      setSlack("");
      setDiscord("");
    } catch (e) {
      toast.error("Failed to load notification settings");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { enabled_events: enabledEvents };
      // Only include URL fields the operator actually edited
      if (slack !== "") payload.slack_webhook_url = slack;
      if (discord !== "") payload.discord_webhook_url = discord;
      await api.put("/system/notifications", payload);
      toast.success("Notification settings saved");
      setTestResult(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/system/notifications/test");
      setTestResult(data);
      const successes = Object.values(data).filter((d) => d.ok).length;
      const failures = Object.values(data).filter((d) => d.configured && !d.ok).length;
      if (successes > 0 && failures === 0) toast.success(`Test fired — ${successes} channel(s) responded OK`);
      else if (failures > 0) toast.error(`${failures} channel(s) failed — see details`);
      else toast.message("No webhooks configured to test");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const toggleEvent = (ev) => {
    setEnabledEvents((cur) =>
      cur.includes(ev) ? cur.filter((e) => e !== ev) : [...cur, ev],
    );
  };

  const clearWebhook = (which) => {
    if (which === "slack") setSlack(" ");      // single-space → server treats as cleared after strip()
    else setDiscord(" ");
  };

  return (
    <div className="p-6 max-w-5xl">
      <PageHeader
        tag="Platform / Config"
        title="Configuration"
        subtitle="Tenant settings, audit-event notifications, and operator policies."
      />

      <div data-testid="notification-settings-card" className="csx-card p-5 mb-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3">
            <Bell className="h-5 w-5 text-fuchsia-300 mt-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300">
                Audit-event Notifications
              </div>
              <div className="text-xs text-slate-400 mt-1 max-w-2xl">
                Push every selected audit event to a Slack or Discord channel in real time. Webhook URLs are stored server-side and never returned in plaintext — only a masked preview is shown.
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <WebhookField
            label="Slack Incoming Webhook"
            placeholder="https://hooks.slack.com/services/T.../B.../..."
            value={slack}
            onChange={setSlack}
            configured={settings?.slack_configured}
            masked={settings?.slack_webhook_url}
            onClear={() => clearWebhook("slack")}
            testId="webhook-slack"
          />
          <WebhookField
            label="Discord Webhook"
            placeholder="https://discord.com/api/webhooks/.../..."
            value={discord}
            onChange={setDiscord}
            configured={settings?.discord_configured}
            masked={settings?.discord_webhook_url}
            onClear={() => clearWebhook("discord")}
            testId="webhook-discord"
          />
        </div>

        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">
            Events to notify on
          </div>
          <div className="space-y-1.5">
            {EVENT_TYPES.map((e) => {
              const on = enabledEvents.includes(e.value);
              return (
                <button
                  key={e.value}
                  data-testid={`event-toggle-${e.value}`}
                  onClick={() => toggleEvent(e.value)}
                  className={[
                    "w-full text-left border p-2.5 flex items-start gap-3 transition-all",
                    on ? "border-fuchsia-400 bg-fuchsia-500/5" : "border-slate-800 hover:border-slate-700",
                  ].join(" ")}
                >
                  <div className={[
                    "w-3.5 h-3.5 mt-0.5 border shrink-0",
                    on ? "border-fuchsia-400 bg-fuchsia-400" : "border-slate-600",
                  ].join(" ")}>
                    {on && <CheckCircle2 className="h-3 w-3 text-slate-950" />}
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-slate-100 font-mono">{e.value}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{e.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex gap-3 pt-3 border-t border-slate-800">
          <TButton data-testid="save-notif-btn" icon={saving ? Loader2 : Save} onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save Settings"}
          </TButton>
          <TButton
            data-testid="test-notif-btn"
            variant="ghost"
            icon={testing ? Loader2 : Send}
            onClick={sendTest}
            disabled={testing || !(settings?.slack_configured || settings?.discord_configured)}
          >
            {testing ? "Sending..." : "Send Test Ping"}
          </TButton>
        </div>

        {testResult && (
          <div data-testid="test-result" className="mt-4 border border-slate-800 p-3 space-y-1.5">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Test result</div>
            {Object.entries(testResult).map(([ch, r]) => (
              <div key={ch} className="flex items-center gap-2 text-xs font-mono">
                {!r.configured ? (
                  <span className="text-slate-500">{ch}: not configured</span>
                ) : r.ok ? (
                  <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /><span className="text-emerald-300">{ch}: OK ({r.status})</span></>
                ) : (
                  <><XCircle className="h-3.5 w-3.5 text-rose-400" /><span className="text-rose-300">{ch}: {r.error || `HTTP ${r.status}`}</span></>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <PerEngagementOverrides />

      <div className="csx-card p-5 mb-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-cyan-300 mb-3">
          <Shield className="h-3 w-3" /> Operator Policies
        </div>
        <ul className="text-xs text-slate-400 space-y-1.5">
          <li>• Authorizations require RBAC role <span className="font-mono text-fuchsia-300">admin</span> or <span className="font-mono text-fuchsia-300">analyst</span></li>
          <li>• Live scans against <span className="font-mono text-rose-300">demo-default</span> are blocked at the API layer</li>
          <li>• Every scope-letter ref is tamper-evident via SHA-256 of the canonical scope content</li>
          <li>• Audit-log rows are insert-only and protected by a unique-index on <span className="font-mono">(engagement_id, at, event_type)</span></li>
        </ul>
      </div>

      <div className="text-[10px] text-slate-600 flex items-center gap-2">
        <AlertTriangle className="h-3 w-3" />
        Webhook URLs grant write access to your channels. Treat them as secrets — rotate via your Slack/Discord admin if leaked.
      </div>
    </div>
  );
};

const WebhookField = ({ label, placeholder, value, onChange, configured, masked, onClear, testId }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">{label}</div>
    <input
      data-testid={testId}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={configured ? `currently: ${masked}` : placeholder}
      className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-xs font-mono outline-none focus:border-fuchsia-500/70"
    />
    <div className="flex items-center justify-between mt-1">
      <span className={[
        "text-[10px]",
        configured ? "text-emerald-400" : "text-slate-500",
      ].join(" ")}>
        {configured ? "● configured" : "○ not configured"}
      </span>
      {configured && (
        <button
          data-testid={`${testId}-clear`}
          onClick={onClear}
          className="text-[10px] text-slate-500 hover:text-rose-300"
        >
          clear
        </button>
      )}
    </div>
  </div>
);

export default Configuration;

// ---------------- Per-Engagement Webhook Overrides ----------------

const PerEngagementOverrides = () => {
  const [engagements, setEngagements] = useState([]);
  const [selected, setSelected] = useState("");
  const [state, setState] = useState(null);
  const [slackInput, setSlackInput] = useState("");
  const [discordInput, setDiscordInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/engagements")
      .then(({ data }) => {
        const live = (data || []).filter((e) => e.id !== "demo-default" && e.status !== "archived");
        setEngagements(live);
        if (live.length && !selected) setSelected(live[0].id);
      })
      .catch(() => toast.error("Failed to load engagements"));
  }, []);

  const load = useCallback(async (eid) => {
    if (!eid) return;
    try {
      const { data } = await api.get(`/engagements/${eid}/notifications`);
      setState(data);
      setSlackInput("");
      setDiscordInput("");
    } catch (e) {
      toast.error("Failed to load engagement overrides");
    }
  }, []);

  useEffect(() => { load(selected); }, [selected, load]);

  const setOverride = async (channel, value) => {
    setSaving(true);
    try {
      const key = `${channel}_webhook_url`;
      await api.put(`/engagements/${selected}/notifications`, { [key]: value });
      toast.success(
        value === "__inherit__" ? `${channel}: reverted to global` :
        value === "" ? `${channel}: suppressed for this engagement` :
        `${channel}: override set`,
      );
      if (channel === "slack") setSlackInput("");
      else setDiscordInput("");
      load(selected);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!engagements.length) {
    return (
      <div data-testid="per-engagement-overrides" className="csx-card p-5 mb-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-cyan-300 mb-2">
          <Building2 className="h-3 w-3" /> Per-Engagement Routing
        </div>
        <div className="text-xs text-slate-500">
          No live engagements found. Create one in <span className="font-mono text-fuchsia-300">/engagements</span> to set per-tenant webhook overrides.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="per-engagement-overrides" className="csx-card p-5 mb-6">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-start gap-3">
          <Building2 className="h-5 w-5 text-cyan-300 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-300">
              Per-Engagement Routing
            </div>
            <div className="text-xs text-slate-400 mt-1 max-w-2xl">
              Route this engagement&apos;s audit events to a dedicated channel — useful when you serve multiple clients and each wants their alerts in their own Slack/Discord workspace. Falls back to the global channel when no override is set.
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Engagement</div>
        <select
          data-testid="override-engagement-select"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-xs font-mono outline-none focus:border-cyan-500/70"
        >
          {engagements.map((e) => (
            <option key={e.id} value={e.id}>{e.name} · {e.client_name}</option>
          ))}
        </select>
      </div>

      {state && (
        <div className="space-y-3">
          {["slack", "discord"].map((ch) => {
            const s = state[ch];
            const input = ch === "slack" ? slackInput : discordInput;
            const setInput = ch === "slack" ? setSlackInput : setDiscordInput;
            return (
              <div key={ch} data-testid={`override-${ch}-row`} className="border border-slate-800 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-mono text-slate-200 capitalize">{ch}</div>
                  <OverrideBadge state={s} />
                </div>
                <input
                  data-testid={`override-${ch}-input`}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={s.masked ? `currently: ${s.masked}` : `https://...`}
                  className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-xs font-mono outline-none focus:border-cyan-500/70 mb-2"
                />
                <div className="flex gap-2 flex-wrap">
                  <TButton
                    data-testid={`override-${ch}-save`}
                    icon={saving ? Loader2 : Save}
                    onClick={() => setOverride(ch, input)}
                    disabled={saving || !input}
                  >
                    Override
                  </TButton>
                  <TButton
                    data-testid={`override-${ch}-suppress`}
                    variant="ghost"
                    icon={BellOff}
                    onClick={() => setOverride(ch, "")}
                    disabled={saving || s.suppressed}
                  >
                    Suppress
                  </TButton>
                  {s.override && (
                    <TButton
                      data-testid={`override-${ch}-inherit`}
                      variant="ghost"
                      icon={RotateCcw}
                      onClick={() => setOverride(ch, "__inherit__")}
                      disabled={saving}
                    >
                      Inherit global
                    </TButton>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const OverrideBadge = ({ state }) => {
  if (state.suppressed) {
    return <span className="text-[10px] text-rose-300 font-mono">○ suppressed for this engagement</span>;
  }
  if (state.override) {
    return <span className="text-[10px] text-cyan-300 font-mono">● override active</span>;
  }
  return (
    <span className="text-[10px] text-slate-500 font-mono">
      {state.configured ? "↳ inheriting global" : "○ no global / no override"}
    </span>
  );
};
